import { hooksApi } from '@verifime/api-definition';
import { reportPageActionToNewRelic } from '@verifime/utils';
import { useEffect, useMemo } from 'react';
import { failureReason } from '../../WorkflowMetadataContext/workflowMetadataReporting';
import {
  buildRequestChooseModel,
  type TRequestChooseIdentity,
} from '../RequestChooseView/requestChooseModel';
import type { TRequestCriteria } from '../requestCriteria';
import { FRESH_READ } from './freshRead';
import {
  NO_SHARED_IDENTITIES,
  useSharedIdentityIds,
  type TSharedIdentityIds,
} from './useSharedIdentityIds';

/**
 * What the request choose screen's reads came to. `loaded` holds everything the rules need: the
 * customer's identities, and which of them already hold an Active permission for the client.
 */
export type TRequestChooseReads =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'loaded';
      identities: TRequestChooseIdentity[];
      sharedIdentityIds: ReadonlySet<string>;
    };

/**
 * New Relic page actions for a screen that cannot show everything it was given. The browser agent
 * does not collect console output in this deployment, so these are what engineering sees. Each
 * carries `source` (a constant label) and `status` (a reason string): never an id, a name, a token,
 * the error object or anything from a response.
 */
export const IDENTITIES_READ_FAILED_PAGE_ACTION = 'request-choose-identities-read-failed';
export const IDENTITIES_HIDDEN_PAGE_ACTION = 'request-choose-identities-hidden';
const IDENTITIES_SOURCE = 'request-choose-identities';

function reportIdentitiesIssue(pageAction: string, message: string, reason: string) {
  console.error(message, { reason });
  reportPageActionToNewRelic(pageAction, { source: IDENTITIES_SOURCE, status: reason });
}

/** The identities of `GET /v1/customer/me` the screen can use: those with an id, name, type and status. */
function readIdentities(customerList: unknown): TRequestChooseIdentity[] {
  if (!Array.isArray(customerList)) {
    return [];
  }
  return customerList.flatMap(
    (customer: {
      id?: string;
      customerName?: string;
      entityType?: string;
      entityStatus?: string;
    }) => {
      const { id, customerName, entityType, entityStatus } = customer;
      return id && customerName && entityType && entityStatus
        ? [{ id, name: customerName, entityType, entityStatus }]
        : [];
    },
  );
}

/**
 * Why identities were hidden, or undefined when none were. Identities with no id, name, type or
 * status never reach the model, so they are told apart from those with a status it does not know.
 */
function hiddenIdentitiesReason(
  hiddenForUnknownStatus: number,
  incompleteCount: number,
): 'incomplete-identity' | 'unknown-status' | undefined {
  if (incompleteCount > 0) {
    return 'incomplete-identity';
  }
  return hiddenForUnknownStatus > 0 ? 'unknown-status' : undefined;
}

type TIdentitiesRead = {
  /** True once this mount has completed a fetch, success or failure. */
  isFetchedAfterMount: boolean;
  /** The read failed, or answered without a list of identities. */
  hasFailed: boolean;
};

/**
 * The screen's state from its two reads. A failure of either is an error, and the identities
 * count only once this mount has fetched them. Otherwise the screen waits until both reads are in.
 */
export function readsStatus(
  identities: TIdentitiesRead,
  sharedStatus: TSharedIdentityIds['status'],
): 'loading' | 'error' | 'loaded' {
  if ((identities.isFetchedAfterMount && identities.hasFailed) || sharedStatus === 'error') {
    return 'error';
  }
  if (!identities.isFetchedAfterMount || sharedStatus === 'loading') {
    return 'loading';
  }
  return 'loaded';
}

/**
 * Reads what the request choose screen needs: the customer's identities (`GET /v1/customer/me`) and
 * which of the ones that fit the request are already shared with the client
 * (`GET /v1/permission?customerId=`, see `useSharedIdentityIds`).
 */
export function useRequestChooseReads(
  criteria: TRequestCriteria,
  organisationId: string,
): TRequestChooseReads {
  const identitiesQuery = hooksApi.useGetV1customerme(
    { queries: { recursive: false } },
    FRESH_READ,
  );
  const customerList = (identitiesQuery.data as { customerList?: unknown } | undefined)
    ?.customerList;
  const identities = useMemo(() => readIdentities(customerList), [customerList]);

  // The identities that fit and have a row are the ones to check against the client.
  const unsharedModel = useMemo(
    () =>
      buildRequestChooseModel({
        criteria,
        identities,
        sharedIdentityIds: NO_SHARED_IDENTITIES,
        // Only the held rows are read here, and just me is not one.
        offersJustMe: false,
        chosenId: null,
      }),
    [criteria, identities],
  );
  const identityIdsToCheck = useMemo(
    () => unsharedModel.heldTypes.flatMap((heldType) => heldType.identities.map(({ id }) => id)),
    [unsharedModel],
  );
  const shared = useSharedIdentityIds(organisationId, identityIdsToCheck);

  // A failed read can still hold the list an earlier read left in the cache, which says nothing
  // about the wallet now, so the failure counts on its own as well as a missing list.
  const hasFailed = !!identitiesQuery.error || !Array.isArray(customerList);
  const status = readsStatus(
    { isFetchedAfterMount: identitiesQuery.isFetchedAfterMount, hasFailed },
    shared.status,
  );

  // The HTTP status, `network-error` or `invalid-response`: never from a message or a body.
  const identitiesReason = identitiesQuery.error
    ? failureReason(identitiesQuery.error)
    : 'invalid-response';
  const hasIdentitiesFailed = identitiesQuery.isFetchedAfterMount && hasFailed;
  useEffect(() => {
    if (hasIdentitiesFailed) {
      reportIdentitiesIssue(
        IDENTITIES_READ_FAILED_PAGE_ACTION,
        'Request choose view could not read the identities',
        identitiesReason,
      );
    }
  }, [hasIdentitiesFailed, identitiesReason]);

  // Seen once the identities are read afresh, not from a cached list.
  const incompleteCount = Array.isArray(customerList) ? customerList.length - identities.length : 0;
  const hiddenReason = hiddenIdentitiesReason(
    unsharedModel.hiddenForUnknownStatus,
    incompleteCount,
  );
  const reasonToReport = identitiesQuery.isFetchedAfterMount ? hiddenReason : undefined;
  useEffect(() => {
    if (reasonToReport) {
      reportIdentitiesIssue(
        IDENTITIES_HIDDEN_PAGE_ACTION,
        'Request choose view hid identities it cannot place',
        reasonToReport,
      );
    }
  }, [reasonToReport]);

  if (status === 'loaded') {
    return { status, identities, sharedIdentityIds: shared.ids };
  }
  return { status };
}
